import { useState, useEffect, useCallback, useRef } from "react";
import { sendQuoteDirect } from '@/utils/quoteDelivery';
import { markProposalSent } from '@/utils/proposalSent';
import type { QuoteSendFormat } from '@/utils/quoteText';
import { toDraftFinancialSummary } from '@/utils/quoteDraftSummary';
import { isRealEventType } from '@/utils/eventType';
import { ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getCartItems, getCartCount, CartItem } from "@/utils/cartStorage";
import CartSheet from "@/components/supplier-menu/CartSheet";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

import { supabase } from "@/integrations/supabase/client";
import { clearCart } from "@/utils/cartStorage";
import { formatDateForStorage, normalizeDate } from "@/utils/dateUtils";
import { useEventPersistence } from "@/hooks/useEventPersistence";
import { useQuoteDraftPersistence } from "@/hooks/useQuoteDraftPersistence";
import { toast } from "sonner";
import { getSupplierColors, loadSupplierCustomization } from "@/utils/supplierCustomizationCache";
import { useSupplierAvailability } from "@/hooks/useSupplierAvailability";
import { parseDateFromStorage } from "@/utils/dateUtils";
import { createShareableStoreUrl } from "@/utils/shareUrl";
import { buildQuoteShareMessage, fetchSupplierShareTemplate } from "@/utils/quoteShareMessage";
import { copyToClipboard } from "@/utils/clipboard";
import ShareLinkFallbackModal from "@/components/supplier-menu/ShareLinkFallbackModal";

/**
 * Floating cart button that appears when there are items in the cart.
 * Listens for storage events and re-checks on interval.
 */
const FloatingCartButton = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isQuoteMode, setIsQuoteMode] = useState(() => searchParams.get('mode') === 'orcamento' || sessionStorage.getItem('ameup_quote_mode') === 'true');
  const { user } = useAuth();
  const [cartItems, setCartItems] = useState<CartItem[]>(() => getCartItems());
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shareFallback, setShareFallback] = useState<{ url: string; message?: string } | null>(null);
  const latestDiscountRef = useRef<{ discount_code?: string; discount_value?: number } | null>(null);
  // Pagamento pós-evento: prazo (30/60/90) escolhido pelo fornecedor pra ESTA
  // proposta (mode=orcamento). Persistido no evento assim que ele existir.
  const [deferredPixDays, setDeferredPixDays] = useState<number | null>(() => {
    const stored = sessionStorage.getItem('ameup_deferred_pix_days');
    return stored ? Number(stored) : null;
  });
  const handleDeferredPixDaysChange = useCallback((days: number | null) => {
    setDeferredPixDays(days);
    if (days) {
      sessionStorage.setItem('ameup_deferred_pix_days', String(days));
    } else {
      sessionStorage.removeItem('ameup_deferred_pix_days');
    }

    // Se a proposta já foi promovida a evento, grava direto — não há mais
    // um novo "promote" pra pendurar essa persistência.
    const promotedEventId = sessionStorage.getItem('ameup_quote_promoted') === 'true'
      ? sessionStorage.getItem('ameup_parent_event_id')
      : null;
    if (!promotedEventId) return;
    (async () => {
      try {
        const { data: eventRow } = await supabase
          .from('events')
          .select('event_date')
          .eq('id', promotedEventId)
          .maybeSingle();
        const eventDate = eventRow?.event_date;
        const dueDate = days && eventDate
          ? new Date(new Date(eventDate as string).getTime() + days * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          : null;
        await supabase
          .from('events')
          .update({ deferred_pix_days: days, deferred_pix_due_date: dueDate })
          .eq('id', promotedEventId);
      } catch (err) {
        console.warn('⚠️ Não foi possível atualizar pagamento pós-evento no evento já promovido', err);
      }
    })();
  }, []);
  const {
    syncCartToSupabase,
    removeChildItem,
    setParentEventId,
    getParentEventId,
    upsertParentEvent,
    markAsShared,
  } = useEventPersistence();
  const { upsertDraftParent, syncCartToDrafts, removeDraftChildItem, getDraftParentId, setDraftParentId, promoteDraftToEvent } = useQuoteDraftPersistence();
  const resolvedSupplierId = loadSupplierCustomization()?.supplierId || cartItems[0]?.supplierId || null;
  const { isDateDisabled, fetchCapacity } = useSupplierAvailability(resolvedSupplierId);

  /** Returns true if the selected event date is blocked; shows toast and returns false otherwise */
  const checkDateAvailability = useCallback((): boolean => {
    const items = getCartItems();
    const dateStr = items[0]?.date;
    if (!dateStr) return true; // no date set yet, let the flow continue
    const parsed = parseDateFromStorage(dateStr);
    if (!parsed) return true;
    if (isDateDisabled(parsed)) {
      toast.error(
        "A data selecionada não está mais disponível para este fornecedor. Por favor, volte e escolha outra data.",
        { duration: 6000 }
      );
      return false;
    }
    return true;
  }, [isDateDisabled]);

  // Restore parentEventId from sessionStorage on mount
  useEffect(() => {
    const stored = sessionStorage.getItem('ameup_parent_event_id');
    if (stored) setParentEventId(stored);
  }, [setParentEventId]);

  // Poll cart count periodically (sessionStorage has no native events within same tab)
  // Check if cart should auto-open (e.g. from "Voltar ao carrinho")
  useEffect(() => {
    const shouldOpen = sessionStorage.getItem('open-cart-on-load');
    if (shouldOpen) {
      sessionStorage.removeItem('open-cart-on-load');
      // Small delay to ensure component is fully mounted
      setTimeout(() => {
        setCartItems(getCartItems());
        setIsCartOpen(true);
      }, 300);
    }
  }, []);

  // Listen for open-cart event
  useEffect(() => {
    const handleOpenCart = () => {
      setCartItems(getCartItems());
      setIsCartOpen(true);
    };
    window.addEventListener('open-cart', handleOpenCart);
    return () => window.removeEventListener('open-cart', handleOpenCart);
  }, []);

  // Listen for cart mutations (add/remove/edit) so the badge and sheet stay in sync
  useEffect(() => {
    const refresh = () => setCartItems(getCartItems());
    window.addEventListener('cart-updated', refresh);
    window.addEventListener('cart-filters-updated', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('cart-updated', refresh);
      window.removeEventListener('cart-filters-updated', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      // Re-check quote mode from sessionStorage
      const currentQuoteMode = searchParams.get('mode') === 'orcamento' || sessionStorage.getItem('ameup_quote_mode') === 'true';
      setIsQuoteMode(currentQuoteMode);

      const items = getCartItems();
      setCartItems(prev => {
        if (JSON.stringify(prev) !== JSON.stringify(items)) return items;
        return prev;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [searchParams]);

  // Helper: ensure parent event exists
  // In quote mode, skip Supabase persistence
  const ensureParentEvent = useCallback(async (): Promise<string | null> => {
    // Quote mode: persist to quote_drafts instead
    if (sessionStorage.getItem('ameup_quote_mode') === 'true') {
      let draftId = getDraftParentId();
      if (draftId) return draftId;
      const stored = sessionStorage.getItem('ameup_draft_parent_id');
      if (stored) { setDraftParentId(stored); return stored; }

      const items = getCartItems();
      const firstItem = items[0];
      if (!firstItem) return null;
      const cepClean = (firstItem.cep || '').replace(/\D/g, '');
      if (cepClean.length !== 8 || !firstItem.date) return null;

      const newDraftId = await upsertDraftParent({
        cep: cepClean,
        eventDate: firstItem.date.split("T")[0],
        guests: firstItem.guests || '0',
        supplierId: firstItem.supplierId || undefined,
        userId: user?.id,
        createdBySupplierId: firstItem.supplierId || undefined,
      });
      return newDraftId;
    }

    let parentId = getParentEventId();
    if (parentId) return parentId;

    const stored = sessionStorage.getItem('ameup_parent_event_id');
    if (stored) {
      setParentEventId(stored);
      return stored;
    }

    // Create parent from first cart item data
    const items = getCartItems();
    const firstItem = items[0];
    if (!firstItem) return null;

    const cepClean = (firstItem.cep || '').replace(/\D/g, '');
    if (cepClean.length !== 8 || !firstItem.date) return null;

    const result = await upsertParentEvent({
      cep: cepClean,
      eventDate: firstItem.date.split("T")[0],
      guests: firstItem.guests || '0',
      supplierId: firstItem.supplierId || undefined,
      userId: user?.id,
      supplierName: firstItem.supplierName || undefined,
      createdBySupplierId: firstItem.supplierId || undefined,
    });
    const id = result?.id || null;
    if (id) {
      sessionStorage.setItem('ameup_parent_event_id', id);
      setParentEventId(id);
    }
    if (result?.eventCode) sessionStorage.setItem('ameup_event_code', result.eventCode);
    return id;
  }, [getParentEventId, setParentEventId, upsertParentEvent, user?.id, getDraftParentId, setDraftParentId, upsertDraftParent]);

  // Sync cart to Supabase (or quote_drafts in quote mode)
  const handleSyncCart = useCallback(async (
    items: CartItem[],
    financialSummary?: { subtotal: number; freight?: number; total?: number; discount_code?: string; discount_value?: number }
  ) => {
    // Store latest discount info for use during share
    if (financialSummary?.discount_code) {
      latestDiscountRef.current = { discount_code: financialSummary.discount_code, discount_value: financialSummary.discount_value };
    } else if (financialSummary && !financialSummary.discount_code) {
      latestDiscountRef.current = null;
    }

    const parentId = await ensureParentEvent();
    if (parentId) {
      if (sessionStorage.getItem('ameup_quote_mode') === 'true') {
        await syncCartToDrafts(items, parentId, user?.id, toDraftFinancialSummary(financialSummary));
      } else {
        await syncCartToSupabase(items, parentId, financialSummary, user?.id);
      }
    }
  }, [ensureParentEvent, syncCartToSupabase, syncCartToDrafts, user?.id]);

  // Remove item handler — also syncs the updated cart summary to the parent event
  const handleRemoveItem = useCallback(async (productId: string) => {
    const parentId = await ensureParentEvent();
    if (parentId) {
      const isQuote = sessionStorage.getItem('ameup_quote_mode') === 'true';
      if (isQuote) {
        await removeDraftChildItem(productId, parentId);
      } else {
        await removeChildItem(productId, parentId);
      }

      // Re-sync the remaining cart items to update parent summary (menu JSON, totals)
      const { getCartItems } = await import('@/utils/cartStorage');
      const remaining = getCartItems();
      const subtotal = remaining.reduce((s: number, i: any) => s + i.totalPrice, 0);
      // Use latest discount info if available
      const disc = latestDiscountRef.current;
      const financialSummary = { subtotal, ...(disc || {}) };

      if (isQuote) {
        await syncCartToDrafts(remaining, parentId, user?.id);
      } else {
        await syncCartToSupabase(remaining, parentId, financialSummary, user?.id, { allowEmpty: true });
      }
    }
  }, [ensureParentEvent, removeChildItem, removeDraftChildItem, syncCartToSupabase, syncCartToDrafts, user?.id]);

  const handleCheckout = async () => {
    const items = getCartItems();
    if (items.length === 0) return;
    if (!checkDateAvailability()) return;
    const isPickupOrder = items.some((item) => item.isPickup === true);
    // Tipo de evento escolhido no simulador (item do carrinho ou filtro da loja).
    const selectedEventType = (() => {
      const fromItems = items.find((item) => isRealEventType(item.eventType))?.eventType;
      if (fromItems) return fromItems;
      try {
        const stored = sessionStorage.getItem('ameup_event_type');
        return isRealEventType(stored) ? stored : null;
      } catch { return null; }
    })();

    setIsLoading(true);
    try {
      // Check if we already have a parent event from the cart flow
      const existingParentId = sessionStorage.getItem('ameup_parent_event_id');

      if (existingParentId) {
        // Ensure cart is synced before status update
        const financialSummary = {
          subtotal: items.reduce((s, i) => s + i.totalPrice, 0),
          // Don't pass total or freight here to preserve existing delivery_fee in DB
        };
        await syncCartToSupabase(items, existingParentId, financialSummary, user?.id);

        // Update existing parent + children status to 'Detalhes da Festa'
        await (supabase as any)
          .from('events')
          .update({ 
            status: 'Detalhes da Festa', 
            geral_status: 'Detalhes da Festa',
            status_payments: 'pending', 
            is_pickup: isPickupOrder,
            ...(selectedEventType ? { event_type: selectedEventType } : {}),
            updated_at: new Date().toISOString() 
          })
          .eq('id', existingParentId);

        await (supabase as any)
          .from('events')
          .update({ 
            status: 'Detalhes da Festa', 
            geral_status: 'Detalhes da Festa',
            updated_at: new Date().toISOString() 
          })
          .eq('parent_event_id', existingParentId)
          .eq('is_active', true);

        navigate(`/infos-festa?simulationId=${existingParentId}${isPickupOrder ? '&pickup=true' : ''}${selectedEventType ? '&skipSteps=1' : ''}`);
        return;
      }

      // Fallback: create parent event via ensureParentEvent (avoids duplicate simulation rows)
      const parentId = await ensureParentEvent();
      if (parentId) {
        // Sync cart items as children
        const financialSummary = {
          subtotal: items.reduce((s, i) => s + i.totalPrice, 0),
          // Don't pass total or freight here to preserve existing delivery_fee in DB
        };
        await syncCartToSupabase(items, parentId, financialSummary, user?.id);

        // Update parent + children status to 'Detalhes da Festa'
        await (supabase as any)
          .from('events')
          .update({ 
            status: 'Detalhes da Festa', 
            geral_status: 'Detalhes da Festa',
            status_payments: 'pending', 
            is_pickup: isPickupOrder,
            ...(selectedEventType ? { event_type: selectedEventType } : {}),
            updated_at: new Date().toISOString() 
          })
          .eq('id', parentId);

        await (supabase as any)
          .from('events')
          .update({ 
            status: 'Detalhes da Festa', 
            geral_status: 'Detalhes da Festa',
            updated_at: new Date().toISOString() 
          })
          .eq('parent_event_id', parentId)
          .eq('is_active', true);

        navigate(`/infos-festa?simulationId=${parentId}${isPickupOrder ? '&pickup=true' : ''}${selectedEventType ? '&skipSteps=1' : ''}`);
      } else {
        toast.error("Erro ao criar simulação. Tente novamente.");
      }
    } catch (error) {
      console.error("Erro ao processar:", error);
      toast.error("Erro ao processar. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleShareQuote = async (action: 'whatsapp' | 'copy' = 'copy', format: QuoteSendFormat = 'carousel') => {
    const currentItems = getCartItems();
    const supplierSlug = currentItems[0]?.supplierSlug || cartItems[0]?.supplierSlug;
    const supplierName = currentItems[0]?.supplierName || cartItems[0]?.supplierName;
    const supplierId = currentItems[0]?.supplierId || cartItems[0]?.supplierId;
    if (!supplierSlug) { return false; }
    if (action === 'whatsapp') {
      if (!user || !supplierId) {
        toast.error('Entre na conta do fornecedor para enviar a proposta.');
        return false;
      }
      const phone = (sessionStorage.getItem('ameup_quote_client_phone') || '').replace(/\D/g, '');
      if (phone.length < 10 || phone.length > 15) {
        toast.error('Informe um telefone válido do cliente antes de enviar.');
        return false;
      }
    }

    // CRITICAL: never generate a share link with an empty cart.
    // Without this guard we'd fall back to a stale sessionStorage event_code
    // (often a tracking-only "Acessou Página" event) producing an empty quote.
    if (currentItems.length === 0) {
      toast.error('Adicione itens ao carrinho antes de compartilhar.');
      return false;
    }
    if (!checkDateAvailability()) { return false; }

    const params = new URLSearchParams();
    try {
      const raw = sessionStorage.getItem(`store_filters_${supplierSlug}`);
      if (raw) {
        const sf = JSON.parse(raw);
        if (sf.cep) params.set('cep', sf.cep.replace(/\D/g, ''));
        if (sf.date) params.set('date', sf.date);
        if (sf.guests) params.set('guests', sf.guests);
      }
    } catch {}

    let refCode: string | null = null;
    let parentIdForShare: string | null = null;
    let draftParentId = getDraftParentId() || sessionStorage.getItem('ameup_draft_parent_id');

    // Ensure a draft parent exists for this cart
    if (!draftParentId) {
      const firstItem = currentItems[0];
      const cepClean = (firstItem.cep || '').replace(/\D/g, '');
      const newDraftId = await upsertDraftParent({
        cep: cepClean,
        eventDate: firstItem.date ? normalizeDate(firstItem.date) : undefined,
        guests: firstItem.guests || '0',
        supplierId: firstItem.supplierId || undefined,
        userId: user?.id,
        createdBySupplierId: firstItem.supplierId || undefined,
      });
      if (newDraftId) {
        setDraftParentId(newDraftId);
        draftParentId = newDraftId;
      }
    }

    if (!draftParentId) {
      toast.error('Não foi possível preparar o proposta. Tente novamente.');
      return false;
    }

    // Always sync the current cart to the draft BEFORE promoting,
    // so the promoted event has every selected item.
    await syncCartToDrafts(currentItems, draftParentId, user?.id, latestDiscountRef.current || undefined);

    const result = await promoteDraftToEvent(draftParentId, supplierName || supplierSlug);
    if (result) {
      setParentEventId(result.parentEventId);
      await markAsShared(result.parentEventId, supplierId);
      refCode = result.eventCode;
      parentIdForShare = result.parentEventId;
      sessionStorage.setItem('ameup_event_code', result.eventCode);
      sessionStorage.setItem('ameup_parent_event_id', result.parentEventId);
      sessionStorage.setItem('ameup_quote_promoted', 'true');

      // Best-effort: grava o prazo de pagamento pós-evento escolhido pelo
      // fornecedor pra ESTA proposta (quote_drafts não possui essa coluna).
      if (deferredPixDays) {
        try {
          const { data: eventRow } = await supabase
            .from('events')
            .select('event_date')
            .eq('id', result.parentEventId)
            .maybeSingle();
          const eventDate = eventRow?.event_date;
          const dueDate = eventDate
            ? new Date(new Date(eventDate as string).getTime() + deferredPixDays * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
            : null;
          await supabase
            .from('events')
            .update({ deferred_pix_days: deferredPixDays, deferred_pix_due_date: dueDate })
            .eq('id', result.parentEventId);
        } catch (err) {
          console.warn('⚠️ Não foi possível gravar pagamento pós-evento no evento promovido', err);
        }
      }
    }

    // Only fall back to sessionStorage if a previous quote was actually promoted.
    // This prevents reusing tracking-only event codes from storefront page views.
    if (!refCode && sessionStorage.getItem('ameup_quote_promoted') === 'true') {
      refCode = sessionStorage.getItem('ameup_event_code');
      parentIdForShare = parentIdForShare || sessionStorage.getItem('ameup_parent_event_id');
    }

    if (!refCode) {
      console.error('❌ Failed to generate refCode for share link. draftParentId:', draftParentId);
      const detailedError = sessionStorage.getItem('ameup_share_error');
      sessionStorage.removeItem('ameup_share_error');
      toast.error(detailedError || 'Não foi possível gerar o link da proposta. Tente novamente.');
      return false;
    }
    sessionStorage.removeItem('ameup_share_error');

    params.set('ref', refCode);
    if (parentIdForShare) params.set('pid', parentIdForShare);
    // Add client name for quote greeting
    const quoteName = sessionStorage.getItem('ameup_quote_client_name');
    if (quoteName) params.set('nome', quoteName);

    const quotedIds = Array.from(new Set(cartItems.map((item) => item.productId).filter(Boolean)));
    if (quotedIds.length > 0) params.set('items', quotedIds.join(','));

    let shareUrl: string | null = null;
    try {
      shareUrl = await createShareableStoreUrl(
        supplierSlug,
        Object.fromEntries(params.entries())
      );
    } catch (err) {
      console.error("❌ createShareableStoreUrl failed:", err);
      toast.error("Não foi possível gerar o link. Tente novamente.");
      return false;
    }

    if (action === 'whatsapp') {
      const rawPhone = (sessionStorage.getItem('ameup_quote_client_phone') || '').replace(/\D/g, '');
      const phone = rawPhone.startsWith('55') ? rawPhone : `55${rawPhone}`;
      if (!supplierId) return false;
      try {
        const sent = await sendQuoteDirect({
          supplierId,
          phone,
          clientName: sessionStorage.getItem('ameup_quote_client_name'),
          proposalUrl: shareUrl,
          items: currentItems,
          format,
        });
        if (!sent) {
          toast.error('Não foi possível enviar a proposta. Verifique a conexão do WhatsApp do fornecedor.');
          return false;
        }
        void markProposalSent(parentIdForShare);
        toast.success('Proposta enviada ao WhatsApp do cliente!');
      } catch {
        toast.error('Não foi possível confirmar o envio. Confira a conversa antes de tentar novamente.');
        return false;
      }
    } else {
      let whatsappMessage: string;
      try {
        const clientName = sessionStorage.getItem('ameup_quote_client_name') || 'Cliente';
        const template = await fetchSupplierShareTemplate(supplierSlug);
        whatsappMessage = buildQuoteShareMessage(clientName, shareUrl, template);
      } catch {
        whatsappMessage = shareUrl;
      }
      const copied = await copyToClipboard(whatsappMessage);
      if (copied) {
        void markProposalSent(parentIdForShare);
        toast.success('Proposta copiada! Já pode colar e enviar para o cliente.');
      } else {
        setShareFallback({ url: shareUrl, message: whatsappMessage });
      }
    }
    setIsCartOpen(true);
  return true;
  };

  // Hide cart on infos-festa and checkout pages (user edits by going back)
  const hiddenRoutes = ['/infos-festa', '/checkout', '/e/'];
  const shouldHide = hiddenRoutes.some(route => location.pathname.startsWith(route));

  // O botão aparece em todas as páginas (inclusive cardápio por seleção), sempre na mesma posição.
  if (cartItems.length === 0 || shouldHide) return null;

  const colors = getSupplierColors();
  const customization = loadSupplierCustomization();
  const whatsappNumber = customization?.whatsappNumber;
  const eventCode = sessionStorage.getItem('ameup_event_code');


  return (
    <>
      <div className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-50 flex flex-col gap-3 items-end">
        {/* WhatsApp Button - hidden in quote mode */}
        {whatsappNumber && !isQuoteMode && (
          <a
            href={`https://wa.me/55${whatsappNumber.replace(/\D/g, "")}${eventCode ? `?text=${encodeURIComponent(`Olá, estou simulando o evento ${eventCode} e tenho uma dúvida.`)}` : ""}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366] text-white shadow-xl hover:scale-110 transition-all active:scale-95"
            aria-label="Contato via WhatsApp"
          >
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="currentColor"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.414 0 .018 5.393 0 12.03a11.854 11.854 0 001.592 5.955L0 24l6.117-1.605a11.803 11.803 0 005.925 1.586h.005c6.635 0 12.032-5.396 12.036-12.031a11.816 11.816 0 00-3.53-8.503" />
            </svg>
          </a>
        )}

        {/* Floating Cart Button */}
        <button
          onClick={() => {
            setCartItems(getCartItems()); // refresh before opening
            setIsCartOpen(true);
          }}
          className="ameup-floating-cart relative hidden md:flex items-center gap-2 rounded-full px-5 py-3 shadow-xl hover:opacity-90 transition-all active:scale-95"
          style={{
            backgroundColor: colors.buttonColor,
            color: colors.buttonTextColor,
          }}
        >
          <ShoppingCart className="h-5 w-5" />
          {(() => {
            const totalCount = cartItems.reduce((sum, it: any) => {
              if (it?.isSelectableMenu) {
                const map = it?.selectedMenuItems && typeof it.selectedMenuItems === 'object'
                  ? Object.values(it.selectedMenuItems as Record<string, number>).reduce((s, q) => s + (Number(q) || 0), 0)
                  : 0;
                return sum + (map || Number(it?.selectedItemsCount) || 1);
              }
              return sum + 1;
            }, 0);
            return (
              <span className="font-semibold text-sm">
                {totalCount} {totalCount === 1 ? 'item' : 'itens'}
              </span>
            );
          })()}
          {/* Pendência: itens no carrinho sem local/data informados */}
          {(() => {
            const requiresCep = !cartItems.some((it: any) => it?.isPickup);
            const missing = cartItems.length > 0 && (
              (requiresCep && (cartItems[0]?.cep || '').replace(/\D/g, '').length !== 8) ||
              !cartItems[0]?.date
            );
            if (!missing) return null;
            return (
              <span
                aria-label="Faltam informações de entrega"
                className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-[11px] font-bold text-white shadow-md"
              >
                !
              </span>
            );
          })()}
        </button>

      </div>

      {/* Celular: faixa fixa no rodapé (valor + Ver Carrinho), no lugar do botão flutuante */}
      {(() => {
        const totalCount = cartItems.reduce((sum, it: any) => {
          if (it?.isSelectableMenu) {
            const map = it?.selectedMenuItems && typeof it.selectedMenuItems === 'object'
              ? Object.values(it.selectedMenuItems as Record<string, number>).reduce((q, v) => q + (Number(v) || 0), 0)
              : 0;
            return sum + (map || Number(it?.selectedItemsCount) || 1);
          }
          return sum + 1;
        }, 0);
        const totalPrice = cartItems.reduce((sum, it: any) => sum + (Number(it?.totalPrice) || 0), 0);
        const requiresCep = !cartItems.some((it: any) => it?.isPickup);
        const missing = cartItems.length > 0 && (
          (requiresCep && (cartItems[0]?.cep || '').replace(/\D/g, '').length !== 8) ||
          !cartItems[0]?.date
        );
        return (
          <div className="ameup-mobile-cart-bar fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background px-4 py-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] shadow-[0_-4px_16px_rgba(0,0,0,0.08)] md:hidden">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 leading-tight">
                <span className="text-lg font-bold text-foreground">
                  {totalPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </span>
                <p className="text-[11px] text-muted-foreground">
                  {totalCount} {totalCount === 1 ? 'item' : 'itens'}
                  {missing && <span className="text-amber-600"> · falta local e data</span>}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setCartItems(getCartItems());
                  setIsCartOpen(true);
                }}
                className="shrink-0 rounded-full px-5 h-10 text-sm font-bold shadow-sm active:scale-95"
                style={{ backgroundColor: colors.buttonColor, color: colors.buttonTextColor }}
              >
                Ver Carrinho
              </button>
            </div>
          </div>
        );
      })()}

      <CartSheet
        open={isCartOpen}
        onOpenChange={setIsCartOpen}
        cartItems={cartItems}
        setCartItems={setCartItems}
        onCheckout={handleCheckout}
        isLoading={isLoading}
        supplierSlug={cartItems[0]?.supplierSlug}
        guestsLabel={(cartItems as any[]).find((it) => typeof it?.guestsLabel === 'string' && it.guestsLabel.trim())?.guestsLabel || null}
        buttonColor={colors.buttonColor}
        buttonTextColor={colors.buttonTextColor}
        onSyncCart={handleSyncCart}
        onRemoveItem={handleRemoveItem}
        isQuoteMode={isQuoteMode}
        onShareQuote={isQuoteMode ? handleShareQuote : undefined}
        deferredPixDays={deferredPixDays}
        onDeferredPixDaysChange={handleDeferredPixDaysChange}
      />

      <ShareLinkFallbackModal
        open={!!shareFallback}
        onOpenChange={(o) => !o && setShareFallback(null)}
        url={shareFallback?.url ?? ""}
        message={shareFallback?.message}
      />
    </>
  );
};

export default FloatingCartButton;
